import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { Status } from '../types/status';

let CACHED_DATA: Record<string, any> = {};

/**
 * Use this hook if your component could use Status [OK, LOADING, ERROR]
 * and the service can be fetched immediately on init
 *
 * The hook can also 'cache' data and will return that on next call to the same url
 * otherwise pass UseCachedData as false to call the service again
 *
 * callService is exposed in case the service is not meant to be called on init
 * the CallServiceOnInit can be passed as false to do this
 *
 * as this is a hook, dont use this/cant be used in code that is not a functional component
 * such as the submit functions/conditional pages
 * so a typical fetcher/axios function will be needed there
 */
const useService = <T>(
    RequestConfig: AxiosRequestConfig,
    UseCachedData: boolean = true,
    CallServiceOnInit: boolean = true
) => {
    const [status, setStatus] = useState<Status>(Status.OK);
    const [data, setData] = useState<T | null>(null);

    const resetData = () => {
        setData(null);
    };

    const callService = async (
        requestConfig: AxiosRequestConfig | undefined = undefined,
        useCachedData: boolean = true
    ) => {
        const _requestConfig = requestConfig ?? RequestConfig;

        try {
            if (!_requestConfig.url) {
                throw new Error('URL not defined');
            }

            const cachedData: T | null | undefined = CACHED_DATA[_requestConfig.url];
            if (cachedData && useCachedData) {
                setData(cachedData);
                return cachedData;
            }

            setStatus(Status.LOADING);
            const response: AxiosResponse<T> = await axios(_requestConfig);
            if (response.status === 200) {
                setData(response.data);
                CACHED_DATA[_requestConfig.url] = response.data;
            }
            setStatus(Status.OK);

            return response.data;
        } catch (error) {
            setStatus(Status.ERROR);
            console.error('Service call failed');
            console.error(' - Error: ', error);
            console.error(' - Payload: ' + JSON.stringify(_requestConfig, null, 4));
        }
        return null;
    };

    useEffect(() => {
        const controller = new AbortController();
        if (CallServiceOnInit) {
            callService(
                {
                    ...RequestConfig,
                    signal: controller.signal
                },
                UseCachedData
            );
        }

        return () => controller.abort();
        // eslint-disable-next-line
    }, [JSON.stringify(RequestConfig)]);

    return {
        data,
        resetData,
        status,
        callService
    };
};

export default useService;
